/**
 * @chefaiq/shared — queryClient
 *
 * Core QueryClient factory shared by all apps.
 * Each app calls createQueryClient() and can pass extra defaultOptions
 * if it needs app-specific behaviour (e.g. mockms adds workspace headers).
 */
import { QueryClient, QueryFunction, DefaultOptions } from "@tanstack/react-query";
import { ApiClient } from "./api-client";

export let API_URL = "";

export function initApiUrl(envUrl: string) {
  API_URL = envUrl || "";
  if (typeof window !== "undefined" && API_URL.startsWith("http://")) {
    API_URL = API_URL.replace("http://", "https://");
  }
}

// ── Auth headers ──────────────────────────────────────────────────────────────

/** Base auth headers. Apps can extend this (e.g. add x-workspace-id). */
export function getBaseAuthHeaders(): HeadersInit {
  const token = ApiClient.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Error handling ────────────────────────────────────────────────────────────

export async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = await res.text();
    let errorMessage = text || res.statusText;
    let responseData: unknown;

    const isHtml =
      text.trim().startsWith("<html") || text.trim().startsWith("<!DOCTYPE");

    if (isHtml) {
      errorMessage = 'Something went wrong on our end. Please try again.';
    } else {
      try {
        const json = JSON.parse(text) as Record<string, unknown>;
        errorMessage =
          (json.message as string) ||
          (json.error as string) ||
          errorMessage;
        responseData = json;
      } catch {
        if (errorMessage.length > 200) errorMessage = errorMessage.slice(0, 200) + "…";
      }
    }

    const error = Object.assign(new Error(errorMessage), {
      status: res.status,
      response: { status: res.status, data: responseData },
    });
    throw error;
  }
}

// ── apiRequest ────────────────────────────────────────────────────────────────

/**
 * Generic fetch wrapper used by hooks.
 * Apps supply their own getAuthHeaders() so app-specific headers
 * (e.g. x-workspace-id) are included automatically.
 */
export async function apiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown,
  customHeaders?: Record<string, string>,
  getHeaders: () => HeadersInit = getBaseAuthHeaders
): Promise<T> {
  const fullUrl = url.startsWith("http") ? url : `${API_URL}${url}`;
  const isFormData = data instanceof FormData;

  const headers: Record<string, string> = {
    ...(getHeaders() as Record<string, string>),
    ...(data && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(customHeaders ?? {}),
  };

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
  });

  if (res.status === 401) {
    localStorage.removeItem("logging_in");
    if (
      !localStorage.getItem("logging_out") &&
      window.location.pathname !== "/signin"
    ) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
      localStorage.removeItem("current-workspace-id");
      localStorage.removeItem("workspaces");
      localStorage.removeItem("subscription_expired");
      document.cookie = "auth_token=; path=/; max-age=0";
      window.location.href = "/signin";
      return null as unknown as T;
    }
  }

  await throwIfResNotOk(res);
  return res.json();
}

// ── QueryClient factory ───────────────────────────────────────────────────────

type UnauthorizedBehavior = "returnNull" | "throw";

export function makeQueryFn<T>(
  getHeaders: () => HeadersInit,
  options: { on401: UnauthorizedBehavior }
): QueryFunction<T> {
  return async ({ queryKey }) => {
    const endpoint = (queryKey as string[]).join("/");
    const fullUrl = endpoint.startsWith("http") ? endpoint : `${API_URL}${endpoint}`;

    const res = await fetch(fullUrl, { headers: getHeaders() });

    if (res.status === 401 || res.status === 403) {
      localStorage.removeItem("logging_in");
      if (options.on401 === "returnNull") return null as T;
      if (
        !localStorage.getItem("logging_out") &&
        window.location.pathname !== "/signin"
      ) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
        localStorage.removeItem("current-workspace-id");
        localStorage.removeItem("workspaces");
        localStorage.removeItem("subscription_expired");
        document.cookie = "auth_token=; path=/; max-age=0";
        window.location.href = "/signin";
        return null as T;
      }
      return null as T;
    }

    await throwIfResNotOk(res);
    return res.json() as T;
  };
}

export function createQueryClient(
  getHeaders: () => HeadersInit = getBaseAuthHeaders,
  extraOptions: Partial<DefaultOptions> = {}
): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        queryFn: makeQueryFn(getHeaders, { on401: "throw" }),
        refetchInterval: false,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
        retry: false,
        ...extraOptions.queries,
      },
      mutations: {
        retry: false,
        ...extraOptions.mutations,
      },
    },
  });
}
